import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Eye as Download } from "lucide-react";
import { abrirVisor } from "@/components/VisorArchivo";
import FiltroSelect from "@/components/FiltroSelect";
import { fechaHoraMX } from "@/lib/format";

export const RESULTADO_CLS = {
  Entregada: "bg-emerald-100 text-emerald-700 border-transparent hover:bg-emerald-100",
  Rechazada: "bg-red-100 text-red-700 border-transparent hover:bg-red-100",
  Incidencia: "bg-amber-100 text-amber-800 border-transparent hover:bg-amber-100",
};

export const detalleIncidencia = (r) => [(r.incidencia?.items || []).map((i) => `${i.tipo}: ${i.atados} atado(s) ${i.modelo}`).join("; "), r.incidencia?.nota ? `Cliente: ${r.incidencia.nota}` : ""].filter(Boolean).join(" · ");

export const descargarArchivoEntrega = (entrega, a) => abrirVisor(`/viajes/entregas/${entrega.id}/archivo/${a.id}`, a.nombre);

export function ArchivosEntrega({ entrega }) {
  return (entrega.archivos || []).map((a, i) => (
    <Button key={a.id} variant="ghost" size="sm" className="h-7 px-2" onClick={() => descargarArchivoEntrega(entrega, a)} data-testid={`entrega-archivo-${entrega.id}-${i}`}>
      <Download className="h-3.5 w-3.5 mr-1" />Hoja {i + 1}
    </Button>
  ));
}

export default function HistorialEntregas({ choferes }) {
  const [rows, setRows] = useState([]);
  const [chofer, setChofer] = useState("todos");
  const [loading, setLoading] = useState(true);
  const cargar = useCallback(() => {
    setLoading(true);
    api.get("/viajes/historial", { params: choferes && chofer !== "todos" ? { chofer_id: chofer } : {} })
      .then(({ data }) => setRows(data || [])).finally(() => setLoading(false));
  }, [chofer, choferes]);
  useEffect(() => { cargar(); }, [cargar]);
  const cuenta = (r) => rows.filter((x) => x.resultado === r).length;
  return (
    <div className="space-y-4" data-testid="historial-entregas">
      <div className="flex flex-wrap items-center gap-3">
        {choferes && <FiltroSelect value={chofer} onChange={setChofer} todosLabel="Todos los choferes" options={choferes.map((c) => ({ value: c.id, label: c.full_name }))} testid="historial-filtro-chofer" />}
        <span className="text-sm text-muted-foreground" data-testid="historial-resumen">
          {rows.length} entrega(s) · <span className="text-emerald-700">{cuenta("Entregada")} entregadas</span> · <span className="text-amber-700">{cuenta("Incidencia")} con incidencia</span> · <span className="text-red-700">{cuenta("Rechazada")} rechazadas</span>
        </span>
      </div>
      <div className="md:hidden space-y-2" data-testid="historial-cards">
        {loading && <p className="text-center text-muted-foreground py-6">Cargando...</p>}
        {!loading && rows.length === 0 && <p className="text-center text-muted-foreground py-6">Sin entregas registradas</p>}
        {rows.map((r) => (
          <div key={r.id} className="rounded-lg border p-3 text-sm space-y-1" data-testid={`historial-card-${r.id}`}>
            <div className="flex justify-between gap-2"><span className="font-semibold">{r.cliente}</span><Badge className={RESULTADO_CLS[r.resultado]}>{r.resultado}</Badge></div>
            <div className="text-xs text-muted-foreground">{fechaHoraMX(r.entregado_at)} · Viaje {r.viaje_folio} · {r.numero_remision}{choferes ? ` · ${r.chofer_nombre}` : ""}</div>
            {(r.motivo || r.nota || r.incidencia) && <div className="text-xs">{[detalleIncidencia(r), r.motivo, r.nota].filter(Boolean).join(" · ")}</div>}
            {r.captura_manual && <Badge variant="outline">manual</Badge>}
            <div><ArchivosEntrega entrega={r} /></div>
          </div>
        ))}
      </div>
      <div className="hidden md:block rounded-lg border overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Fecha y hora</TableHead><TableHead>Viaje</TableHead><TableHead>Remisión</TableHead>
            <TableHead>Cliente</TableHead>{choferes && <TableHead>Chofer</TableHead>}<TableHead>Resultado</TableHead><TableHead>Remisión escaneada</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">Cargando...</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground" data-testid="historial-vacio">Sin entregas registradas</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`historial-fila-${r.id}`}>
                <TableCell className="whitespace-nowrap">{fechaHoraMX(r.entregado_at)}</TableCell>
                <TableCell>{r.viaje_folio}</TableCell>
                <TableCell>{r.numero_remision}<div className="text-xs text-muted-foreground">{r.numero_pedido}</div></TableCell>
                <TableCell className="min-w-[160px]">{r.cliente}</TableCell>
                {choferes && <TableCell>{r.chofer_nombre}</TableCell>}
                <TableCell>
                  <Badge className={RESULTADO_CLS[r.resultado]}>{r.resultado}</Badge>
                  {r.captura_manual && <Badge variant="outline" className="ml-1">manual</Badge>}
                  {(r.motivo || r.nota || r.incidencia) && <div className="text-xs text-muted-foreground mt-1 max-w-[220px]">{[detalleIncidencia(r), r.motivo, r.nota].filter(Boolean).join(" · ")}</div>}
                </TableCell>
                <TableCell><ArchivosEntrega entrega={r} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
